import { useContext, useEffect } from 'react';
import { ConfirmationDialog } from 'components/Dialogs';
import { ConfirmUnsavedChanges } from 'components/Spreadsheet/components';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import type { DialogAction } from 'providers/UnsavedChangesProvider/UnsavedChangesProvider.props';
import { useCrossReferenceStore } from 'store/useCrossReferenceStore';
import type { CrossReferenceProviderProps } from './CrossReferenceProvider.props';

// Wraps a host page that uses CrossReferenceValueViewer. Publishes cross-ref
// dirtiness into the page's UnsavedChangesContext so attempted navigation
// triggers the standard confirm-leave dialog. Store hygiene across captables
// is handled by useHydrateCaptableCrossReferences, which fully replaces the
// states map on every captable load — no unmount cleanup needed here.
//
// Each popup builds its own SpreadsheetConfig + useWorkbook locally (see
// CrossReferencePopupSheet), so this provider does not need to plumb a
// shared workbook through context — it only owns the unsaved-changes wiring.
const CrossReferenceProvider: React.FC<CrossReferenceProviderProps> = ({ children }) => {
  const isAnyDirty = useCrossReferenceStore(store => store.isAnyDirty());
  const { setAction } = useContext(UnsavedChangesContext);

  useEffect(() => {
    if (isAnyDirty) {
      setAction({
        wrapper: ConfirmationDialog,
        content: ConfirmUnsavedChanges,
      } as unknown as DialogAction);
    } else {
      setAction(false);
    }
  }, [isAnyDirty, setAction]);

  // Intentionally no reset-on-unmount: the save flow causes a transient early
  // return in the host page which would unmount-remount this provider and wipe
  // a store that was just hydrated with the server's saved values. Store
  // hygiene across captables is already guaranteed by useHydrateCaptableCross-
  // References, which replaces the entire `states` map on every captable load.
  return <>{children}</>;
};

export default CrossReferenceProvider;
